'use client';

import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import type React from 'react';
import { Button } from './button';
import { Link } from './link';

export function Dropdown(props: Headless.MenuProps) {
  return <Headless.Menu {...props} />;
}

export function DropdownButton<T extends React.ElementType = typeof Button>({
  as = Button,
  ...props
}: { className?: string } & Omit<Headless.MenuButtonProps<T>, 'className'>) {
  return <Headless.MenuButton as={as} {...props} />;
}

export function DropdownMenu({
  anchor = 'bottom',
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuItemsProps, 'as' | 'className'>) {
  return (
    <Headless.MenuItems
      {...props}
      transition
      anchor={anchor}
      className={clsx(
        className,
        // Anchor positioning
        'tw:[--anchor-gap:theme(spacing.2)] tw:[--anchor-padding:theme(spacing.1)] tw:data-[anchor~=start]:[--anchor-offset:-6px] tw:data-[anchor~=end]:[--anchor-offset:6px] tw:sm:data-[anchor~=start]:[--anchor-offset:-4px] tw:sm:data-[anchor~=end]:[--anchor-offset:4px]',
        // Base styles
        'tw:isolate tw:w-max tw:rounded-xl tw:p-1',
        // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
        'tw:outline tw:outline-1 tw:outline-transparent tw:focus:outline-none',
        // Handle scrolling when menu won't fit in viewport
        'tw:overflow-y-auto',
        // Popover background
        'tw:bg-white/75 tw:backdrop-blur-xl tw:dark:bg-zinc-800/75',
        // Shadows
        'tw:shadow-lg tw:ring-1 tw:ring-zinc-950/10 tw:dark:ring-inset tw:dark:ring-white/10',
        // Define grid at the menu level if subgrid is supported
        'tw:supports-[grid-template-columns:subgrid]:grid tw:supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]',
        // Transitions
        'tw:transition tw:data-[closed]:data-[leave]:opacity-0 tw:data-[leave]:duration-100 tw:data-[leave]:ease-in'
      )}
    />
  );
}

export function DropdownItem({
  className,
  ...props
}: { className?: string } & (
  | Omit<Headless.MenuItemProps<'button'>, 'as' | 'className'>
  | Omit<Headless.MenuItemProps<typeof Link>, 'as' | 'className'>
)) {
  const classes = clsx(
    className,
    // Base styles
    'tw:group tw:cursor-default tw:rounded-lg tw:px-3.5 tw:py-2.5 tw:focus:outline-none tw:sm:px-3 tw:sm:py-1.5',
    // Text styles
    'tw:text-left tw:text-base/6 tw:text-zinc-950 tw:sm:text-sm/6 tw:dark:text-white tw:forced-colors:text-[CanvasText]',
    // Focus
    'tw:data-[focus]:bg-brand-green-1/20 tw:data-[focus]:text-zinc-950',
    // Disabled state
    'tw:data-[disabled]:opacity-50',
    // Forced colors mode
    'tw:forced-color-adjust-none tw:forced-colors:data-[focus]:bg-[Highlight] tw:forced-colors:data-[focus]:text-[HighlightText] tw:forced-colors:[&>[data-slot=icon]]:data-[focus]:text-[HighlightText]',
    // Use subgrid when available but fallback to an explicit grid layout if not
    'tw:col-span-full tw:grid tw:grid-cols-[auto_1fr_1.5rem_0.5rem_auto] tw:items-center tw:supports-[grid-template-columns:subgrid]:grid-cols-subgrid',
    // Icons
    'tw:[&>[data-slot=icon]]:col-start-1 tw:[&>[data-slot=icon]]:row-start-1 tw:[&>[data-slot=icon]]:-ml-0.5 tw:[&>[data-slot=icon]]:mr-2.5 tw:[&>[data-slot=icon]]:size-5 tw:sm:[&>[data-slot=icon]]:mr-2 tw:sm:[&>[data-slot=icon]]:sm:size-4',
    'tw:[&>[data-slot=icon]]:text-zinc-500 tw:[&>[data-slot=icon]]:data-[focus]:text-white tw:[&>[data-slot=icon]]:dark:text-zinc-400 tw:[&>[data-slot=icon]]:data-[focus]:dark:text-white',
    // Avatar
    'tw:[&>[data-slot=avatar]]:-ml-1 tw:[&>[data-slot=avatar]]:mr-2.5 tw:[&>[data-slot=avatar]]:size-6 tw:sm:[&>[data-slot=avatar]]:mr-2 tw:sm:[&>[data-slot=avatar]]:size-5'
  );

  return 'href' in props ? (
    <Headless.MenuItem as={Link} {...props} className={classes} />
  ) : (
    <Headless.MenuItem as="button" type="button" {...props} className={classes} />
  );
}

type DropdownHeaderProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

export function DropdownHeader({ className, ...props }: DropdownHeaderProps) {
  return (
    <div
      {...props}
      className={clsx(className, 'tw:col-span-5 tw:px-3.5 tw:pb-1 tw:pt-2.5 tw:sm:px-3')}
    />
  );
}

export function DropdownSection({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuSectionProps, 'as' | 'className'>) {
  return (
    <Headless.MenuSection
      {...props}
      className={clsx(
        className,
        // Define grid at the section level instead of the item level if subgrid is supported
        'tw:col-span-full tw:supports-[grid-template-columns:subgrid]:grid tw:supports-[grid-template-columns:subgrid]:grid-cols-[auto_1fr_1.5rem_0.5rem_auto]'
      )}
    />
  );
}

export function DropdownHeading({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuHeadingProps, 'as' | 'className'>) {
  return (
    <Headless.MenuHeading
      {...props}
      className={clsx(
        className,
        'tw:col-span-full tw:grid tw:grid-cols-[1fr,auto] tw:gap-x-12 tw:px-3.5 tw:pb-1 tw:pt-2 tw:text-sm/5 tw:font-medium tw:text-zinc-500 tw:sm:px-3 tw:sm:text-xs/5 tw:dark:text-zinc-400'
      )}
    />
  );
}

export function DropdownDivider({
  className,
  ...props
}: { className?: string } & Omit<Headless.MenuSeparatorProps, 'as' | 'className'>) {
  return (
    <Headless.MenuSeparator
      {...props}
      className={clsx(
        className,
        'tw:col-span-full tw:mx-3.5 tw:my-1 tw:h-px tw:border-0 tw:bg-zinc-950/5 tw:sm:mx-3 tw:dark:bg-white/10 tw:forced-colors:bg-[CanvasText]'
      )}
    />
  );
}

export function DropdownLabel({
  className,
  ...props
}: { className?: string } & Omit<Headless.LabelProps, 'as' | 'className'>) {
  return (
    <Headless.Label
      {...props}
      data-slot="label"
      className={clsx(className, 'tw:col-start-2 tw:row-start-1')}
      {...props}
    />
  );
}

export function DropdownDescription({
  className,
  ...props
}: { className?: string } & Omit<Headless.DescriptionProps, 'as' | 'className'>) {
  return (
    <Headless.Description
      data-slot="description"
      {...props}
      className={clsx(
        className,
        'tw:col-span-2 tw:col-start-2 tw:row-start-2 tw:text-sm/5 tw:text-zinc-500 tw:group-data-[focus]:text-white tw:sm:text-xs/5 tw:dark:text-zinc-400 tw:forced-colors:group-data-[focus]:text-[HighlightText]'
      )}
    />
  );
}

export function DropdownShortcut({
  keys,
  className,
  ...props
}: { keys: string | string[]; className?: string } & Omit<
  Headless.DescriptionProps<'kbd'>,
  'as' | 'className'
>) {
  return (
    <Headless.Description
      as="kbd"
      {...props}
      className={clsx(className, 'tw:col-start-5 tw:row-start-1 tw:flex tw:justify-self-end')}
    >
      {(Array.isArray(keys) ? keys : keys.split('')).map((char, index) => (
        <kbd
          key={index}
          className={clsx([
            'tw:min-w-[2ch] tw:text-center tw:font-sans tw:capitalize tw:text-zinc-400 tw:group-data-[focus]:text-white tw:forced-colors:group-data-[focus]:text-[HighlightText]',
            // Make sure key names that are longer than one character (like "Tab") have extra space
            index > 0 && char.length > 1 && 'tw:pl-1',
          ])}
        >
          {char}
        </kbd>
      ))}
    </Headless.Description>
  );
}
